import Checkout from './Checkout';
import CheckoutAddress from './CheckoutAddress';
import CheckoutError from './CheckoutError';
import CheckoutPayment from './CheckoutPayment';
import CheckoutSettings from './CheckoutSettings';
import CheckoutShipment from './CheckoutShipment';
import OrderMetadataOption from './OrderMetadataOption';
import SavedPaymentMethod from './SavedPaymentMethod';
import checkoutService from './checkoutService';
import paymentTypes from './paymentTypes';
import { checkoutSteps } from './checkoutErrorCodes';
import {
    buildContactPatch,
    buildPaymentInfo,
    buildPaymentPatch,
    buildShippingFlagsPatch,
    buildShippingServicePatch,
} from './checkoutPatches';
import { buildRateList, findDefaultRate } from './checkoutRates';
import restorePayment from './checkoutPaymentRestore';
import paymentMethodService, { UPDATABLE_FIELDS } from './paymentMethodService';
import clientInputError from '../errors/clientInputError';
import Order from '../Order/Order';
import Project from '../Project/Project';
import projectService from '../Project/projectService';
import UserAddress from '../User/UserAddress';
import userAddressService from '../User/userAddressService';
import userService from '../User/userService';
import Country from '../Shipping/Country';
import State from '../Shipping/State';
import SiteSettings from '../Site/SiteSettings';
import siteSettingsService from '../Site/siteSettingsService';
import shippingQuoteService from '../Shipping/shippingQuoteService';
import { typeCheck } from '../lib/util';

const NO_WEIGHT = 0;

function toAddresses (addressData) {
    return Object.freeze((addressData || []).map(address => new UserAddress(address)));
}

/*
 * Countries and states come from the project's own shipping endpoint rather than the checkout rate call,
 * because that is the only storefront-reachable source for them. Note these carry no shipTo/billTo
 * flags - those live on a separate service outside /rest/storefront - so the list is not pre-filtered
 * by what a country is allowed to be used for.
 */

function toGeography (shippingData) {
    return {
        countries: Object.freeze(((shippingData && shippingData.countries) || []).map(country => new Country(country))),
        states: Object.freeze(((shippingData && shippingData.states) || []).map(state => new State(state))),
    };
}

/*
 * An address selected earlier can vanish from the book - deleted from another tab, or on the account
 * page. The setters only validate on assignment, so stale selections are cleared explicitly rather
 * than left pointing at something that no longer exists.
 */

function dropMissingAddressSelections (checkout) {
    [
        ['shipToAddress', 'shippingAddresses'],
        ['shipFromAddress', 'shippingAddresses'],
        ['billToAddress', 'billingAddresses'],
    ].forEach(([field, bookName]) => {
        const selected = checkout[field];

        if (selected && !checkout[bookName].some(candidate => UserAddress.matches(candidate, selected))) {
            console.warn(`Collaterate.Checkout: the address selected for ${field} is no longer in ${bookName}. Cleared.`);
            checkout[field] = null;
        }
    });
}

function toCheckoutError (e) {
    return Promise.reject(new CheckoutError(e));
}

/**
 * SDK interface for completing a checkout without leaving your own pages.
 *
 * A checkout is a {@link Project Project} the shopper is buying. Start one, render your form from the
 * collections on the returned {@link Checkout Checkout}, assign the shopper's choices, save each step,
 * then submit.
 *
 * **Payment.** The SDK never loads or calls Braintree. Three of the payment paths need no Braintree
 * JavaScript at all: terms/PO, a zero total, and any saved card or vaulted PayPal account from
 * {@link Collaterate.Checkout.getSavedPaymentMethods getSavedPaymentMethods}. Entering a *new* card is
 * the only case where you bootstrap `braintree-web` yourself and hand the resulting nonce to
 * {@link Collaterate.Checkout.setPayment setPayment}. Enrolling a new PayPal account is not supported.
 *
 * **The shopper's wallet.** Cards can be vaulted, changed and removed from here too, with
 * {@link Collaterate.Checkout.savePaymentMethod savePaymentMethod},
 * {@link Collaterate.Checkout.updatePaymentMethod updatePaymentMethod} and
 * {@link Collaterate.Checkout.deletePaymentMethod deletePaymentMethod}, so an account page can reuse
 * this namespace outside a checkout.
 *
 * **Authentication.** Checkout requires a signed-in user. Use
 * {@link Collaterate.User.login Collaterate.User.login} first.
 *
 * @namespace Collaterate.Checkout
 */

export default class CheckoutAPI {
    /**
     * Start a checkout for a project, loading everything needed to render the whole flow in one call.
     *
     * @param {number} projectId - The id of the project to check out.
     * @returns {Promise<Checkout>} Promise that resolves with a {@link Checkout Checkout}.
     * @throws {CollaterateResponseError} If no user is signed in, or the project is not the shopper's.
     * @memberof Collaterate.Checkout
     * @example
     * const checkout = await Collaterate.Checkout.start(projectId);
     *
     * console.log(`Shipping to one of ${checkout.shippingAddresses.length} saved addresses.`);
     * console.log(`Order total: ${checkout.project.displayTotalPrice}`);
     */

    static async start (projectId) {
        typeCheck(projectId, Number);

        const [
            projectData,
            user,
            siteSettingsData,
            shippingConfigurationData,
            shippingDefaults,
            shippingAddressData,
            billingAddressData,
            orderMetadataData,
            shipmentData,
            creditState,
            shippingData,
            creditCardData,
        ] = await Promise.all([
            projectService.getById(projectId),
            userService.getUser(),
            siteSettingsService.getSettings(),
            checkoutService.getSiteShippingConfiguration(),
            shippingQuoteService.getDefaults(),
            userAddressService.getShippingAddresses(),
            userAddressService.getBillingAddresses(),
            checkoutService.getOrderMetadataOptions(),
            projectService.getShipments(projectId),
            userService.getCreditState(projectId),
            projectService.getShipping(projectId),
            projectService.getCreditCard(projectId),
        ]);

        const savedPaymentMethods = user
            ? await CheckoutAPI.getSavedPaymentMethods(user.id)
            : [];

        const settings = new CheckoutSettings({
            // WrapperDTO<BasicSiteShippingConfigurationDTO> - a single object, despite the plural path.
            shippingConfiguration: shippingConfigurationData || {},
            shippingDefaults: shippingDefaults || {},
            siteSettings: new SiteSettings(siteSettingsData || {}),
        });

        const shipment = (shipmentData || [])[0];

        const checkout = new Checkout({
            project: new Project(projectData),
            user,
            settings,
            shipment: shipment ? new CheckoutShipment(shipment) : null,
            rates: [],
            ...toGeography(shippingData),
            shippingAddresses: toAddresses(shippingAddressData),
            billingAddresses: toAddresses(billingAddressData),
            orderMetadataOptions: Object.freeze((orderMetadataData || []).map(option => new OrderMetadataOption(option))),
            savedPaymentMethods,
            creditBalance: (creditState && creditState.userCreditBalance) || 0,
        });

        /*
         * Assigned through the setter rather than passed in, because Checkout always starts with no
         * payment and the setter is what validates it. Null here is the ordinary case: nothing chosen
         * yet, or a newly entered card whose nonce cannot be restored.
         */
        checkout.payment = restorePayment(projectData, creditCardData);

        return checkout;
    }

    /**
     * Re-read the project and shipment from the server. Call this after a save to pick up recalculated
     * totals, and before showing an order review.
     *
     * @param {Checkout} checkout - The checkout to refresh. Updated in place.
     * @returns {Promise<Checkout>} Promise that resolves with the same {@link Checkout Checkout}.
     * @memberof Collaterate.Checkout
     * @example
     * await Collaterate.Checkout.saveShipping(checkout);
     * await Collaterate.Checkout.refresh(checkout);
     *
     * console.log(`New total with shipping: ${checkout.project.displayTotalPrice}`);
     */

    static async refresh (checkout) {
        typeCheck(checkout, Checkout);

        const [projectData, shipmentData] = await Promise.all([
            projectService.getById(checkout.project.id),
            projectService.getShipments(checkout.project.id),
        ]);

        const shipment = (shipmentData || [])[0];

        /*
         * `project` is read only to consumers, so the reloaded copy is installed rather than assigned.
         * The constructor marks it configurable for exactly this; redefining leaves that attribute in
         * place, so refreshing repeatedly is fine.
         */
        Object.defineProperty(checkout, 'project', {
            value: new Project(projectData),
            enumerable: true,
        });
        Object.assign(checkout, { shipment: shipment ? new CheckoutShipment(shipment) : null });

        return checkout;
    }

    /**
     * Re-read both of the shopper's address books.
     *
     * Call this when addresses may have changed outside this checkout - edited on an account page, or
     * removed in another tab. Any selected address that no longer exists is cleared.
     *
     * @param {Checkout} checkout - The checkout to refresh. Updated in place.
     * @returns {Promise<Checkout>} Promise that resolves with the same {@link Checkout Checkout}.
     * @memberof Collaterate.Checkout
     * @example
     * await Collaterate.Checkout.refreshAddresses(checkout);
     *
     * renderAddressPicker(checkout.shippingAddresses);
     */

    static async refreshAddresses (checkout) {
        typeCheck(checkout, Checkout);

        const [shippingAddressData, billingAddressData] = await Promise.all([
            userAddressService.getShippingAddresses(),
            userAddressService.getBillingAddresses(),
        ]);

        Object.assign(checkout, {
            shippingAddresses: toAddresses(shippingAddressData),
            billingAddresses: toAddresses(billingAddressData),
        });

        dropMissingAddressSelections(checkout);

        return checkout;
    }

    /**
     * Start entering a new address for this checkout.
     *
     * Returns a {@link CheckoutAddress CheckoutAddress} whose country and state fields cascade for you:
     * assigning a country clears any state already chosen, empties the stale state list, and drops the
     * postal code for countries that do not use one. Bind your form to it, then save it with
     * {@link Collaterate.Checkout.addAddress addAddress}.
     *
     * @param {Checkout} checkout - The checkout the address is being entered for.
     * @param {Object} [initialValues] - Any fields to prefill, using {@link CheckoutAddress CheckoutAddress} property names. A `country` prefills its state list too.
     * @returns {Promise<CheckoutAddress>} Promise that resolves with a {@link CheckoutAddress CheckoutAddress}.
     * @memberof Collaterate.Checkout
     * @example
     * const form = await Collaterate.Checkout.createAddressForm(checkout, { addressType: 'SHIP_TO' });
     *
     * renderCountrySelect(form.countries);
     */

    static async createAddressForm (checkout, initialValues = {}) {
        typeCheck(checkout, Checkout);
        typeCheck(initialValues, Object);

        const form = new CheckoutAddress({
            ...initialValues,
            projectId: checkout.project.id,
            countries: checkout.countries,
        });

        if (form.country) {
            await CheckoutAPI.refreshStates(form);
        }

        return form;
    }

    /**
     * Load the state list for the country currently selected on an address form.
     *
     * Call this after assigning {@link CheckoutAddress#country country}. You do not need to clear the
     * previously selected state - assigning the country already did that.
     *
     * A country with no regions resolves to an empty list without a request, so an empty
     * {@link CheckoutAddress#states states} means "no state field needed".
     *
     * @param {CheckoutAddress} address - The address form to load states onto. Updated in place.
     * @returns {Promise<State[]>} Promise that resolves with the country's {@link State State}s.
     * @memberof Collaterate.Checkout
     * @example
     * async function onCountryChange (country) {
     *     address.country = country;   // clears state and any stale postal code for you
     *
     *     const states = await Collaterate.Checkout.refreshStates(address);
     *
     *     states.length ? renderStateSelect(states) : hideStateField();
     *     country.hasPostalCodes ? showPostalCodeField() : hidePostalCodeField();
     * }
     */

    static async refreshStates (address) {
        typeCheck(address, CheckoutAddress);

        if (!address.country) {
            console.warn('Collaterate.Checkout.refreshStates() needs a country to be selected. No states loaded.');
            return address.states;
        }

        if (!address.country.hasRegions) {
            Object.assign(address, { states: [] });

            return address.states;
        }

        const shippingData = await projectService.getShipping(address.projectId, address.country.id);

        Object.assign(address, { states: toGeography(shippingData).states });

        return address.states;
    }

    /**
     * Load the states, provinces or regions for a country, for a country/state pair in an address form.
     *
     * This is the low level lookup, for consumers managing their own form state. Prefer
     * {@link Collaterate.Checkout.createAddressForm createAddressForm}, which cascades the country and
     * state fields for you so nothing has to be cleared by hand.
     *
     * {@link Checkout#states Checkout#states} is not this: it tracks whatever country the checkout last
     * quoted shipping for, so it does not follow a country the shopper is choosing in a form.
     *
     * Countries that have no regions resolve to an empty list without a request, so an empty result
     * means "no state field needed" rather than "nothing found".
     *
     * @param {Checkout} checkout - The checkout the address is being entered for.
     * @param {Country} country - One of {@link Checkout#countries Checkout#countries}.
     * @returns {Promise<State[]>} Promise that resolves with the country's {@link State State}s, empty when it has none.
     * @memberof Collaterate.Checkout
     * @example
     * // When the shopper picks a country in your new-address form
     * async function onCountryChange (country) {
     *     form.stateId = null;
     *     form.countryId = country.id;
     *
     *     const states = await Collaterate.Checkout.getStatesForCountry(checkout, country);
     *
     *     if (states.length) {
     *         renderStateSelect(states);
     *     } else {
     *         hideStateField();
     *     }
     *
     *     country.hasPostalCodes ? showPostalCodeField() : hidePostalCodeField();
     * }
     */

    static async getStatesForCountry (checkout, country) {
        typeCheck(checkout, Checkout);
        typeCheck(country, Country);

        // Skip the round trip for countries the API has no regions for.
        if (!country.hasRegions) {
            return Object.freeze([]);
        }

        const shippingData = await projectService.getShipping(checkout.project.id, country.id);

        return toGeography(shippingData).states;
    }

    /**
     * Add a new address to the shopper's address book, mid-checkout, and make it immediately selectable.
     *
     * This is how a shopper ships somewhere they have not shipped before. Creating the address with
     * {@link Collaterate.User.createAddress Collaterate.User.createAddress} alone is not enough - the
     * checkout validates selections against its own copy of the address book, so use this instead and
     * the book is refreshed for you.
     *
     * Which book the new address lands in is decided by its `addressType`: `"SHIP_TO"` and
     * `"BLIND_FROM"` go to {@link Checkout#shippingAddresses shippingAddresses}, `"BILL_TO"` to
     * {@link Checkout#billingAddresses billingAddresses}.
     *
     * @param {Checkout} checkout - The checkout to add the address to. Updated in place.
     * @param {CheckoutAddress|Collaterate.User~addressOptions} addressOptions - The address to add. Either a {@link CheckoutAddress CheckoutAddress} from {@link Collaterate.Checkout.createAddressForm createAddressForm}, or a plain object.
     * @returns {Promise<UserAddress>} Promise that resolves with the new {@link UserAddress UserAddress}, ready to assign.
     * @throws {CollaterateInputErrorList} If any field fails validation.
     * @memberof Collaterate.Checkout
     * @example
     * // From an address form, with the country and state cascade handled for you
     * const form = await Collaterate.Checkout.createAddressForm(checkout, { addressType: 'SHIP_TO' });
     *
     * form.firstName = 'Dan';
     * form.country = form.countries[0];
     * await Collaterate.Checkout.refreshStates(form);
     * form.state = form.states[0];
     *
     * checkout.shipToAddress = await Collaterate.Checkout.addAddress(checkout, form);
     *
     * @example
     * // Or from a plain object, if you are managing the form yourself
     * const address = await Collaterate.Checkout.addAddress(checkout, {
     *     firstName: 'Dan',
     *     lastName: 'Piscitiello',
     *     company: 'The Bernard Group',
     *     address: '315 Walnut St',
     *     city: 'Chanhassen',
     *     stateId: 24,
     *     countryId: 1,
     *     postalCode: '55317',
     *     phone: '555-0100',
     *     addressType: 'SHIP_TO',
     * });
     *
     * checkout.shipToAddress = address;
     *
     * await Collaterate.Checkout.refreshRates(checkout);
     */

    static async addAddress (checkout, addressOptions) {
        typeCheck(checkout, Checkout);
        typeCheck(addressOptions, Object);

        const created = new UserAddress(await userAddressService.createAddress(UserAddress.toDB(addressOptions)));

        await CheckoutAPI.refreshAddresses(checkout);

        /*
         * Prefer the copy that is actually in the refreshed book: it carries whatever the server
         * resolved or normalized, and it is the instance a consumer will see in their own picker.
         */
        const inBook = [...checkout.shippingAddresses, ...checkout.billingAddresses]
            .find(candidate => UserAddress.matches(candidate, created));

        if (!inBook) {
            console.warn('Collaterate.Checkout.addAddress: the new address is not in either address book. Check its addressType.');
        }

        return inBook || created;
    }

    /**
     * Fetch the selectable shipping rates for the checkout's current destination, and preselect one.
     *
     * Call this whenever the ship-to address changes. If nothing is selected yet, the cheapest carrier
     * service is chosen so the order summary is never blank; an existing selection is kept when it is
     * still available and dropped when it is not.
     *
     * The whole of {@link Checkout#shipToAddress shipToAddress} is quoted, not just its postal code, so
     * carrier services that differ by destination type are priced for the address the shopper actually
     * chose - a residential address quotes FedEx Home Delivery where a commercial one quotes FedEx
     * Ground. Rates for an address still missing its street line or city fall back to the destination
     * saved on the project.
     *
     * The list holds more than the quoted carrier services. A flat-rate USPS option is included for
     * sample-request carts, and a "describe it yourself" option on sites that enable one. Neither is
     * ever auto-selected. Check {@link CheckoutRate#isCustomService isCustomService} - if the shopper
     * picks that one, set {@link Checkout#customShippingDescription customShippingDescription} before
     * saving.
     *
     * @param {Checkout} checkout - The checkout to refresh rates for. Updated in place.
     * @returns {Promise<CheckoutRate[]>} Promise that resolves with the available {@link CheckoutRate CheckoutRate}s.
     * @memberof Collaterate.Checkout
     * @example
     * checkout.shipToAddress = checkout.shippingAddresses[0];
     *
     * const rates = await Collaterate.Checkout.refreshRates(checkout);
     *
     * rates.forEach(rate => console.log(`${rate.name}: ${rate.displayPrice}`));
     * console.log(`Preselected: ${checkout.shippingService.name}`);
     */

    static async refreshRates (checkout) {
        typeCheck(checkout, Checkout);

        const address = checkout.shipToAddress;

        if (!address) {
            console.warn('Collaterate.Checkout.refreshRates() needs a shipToAddress. No rates fetched.');
            return checkout.rates;
        }

        const previousSelectionKey = checkout.shippingService ? checkout.shippingService.key : null;

        const [quoteResponse, shippingData] = await Promise.all([
            shippingQuoteService.getQuotes({
                projectId: checkout.project.id,
                countryId: address.country ? address.country.id : null,
                stateId: address.state ? address.state.id : null,
                postalCode: address.postalCode,
                /*
                 * The street line and city are what make the quote reflect *this* address rather than
                 * the one saved on the project: without both of them ShippingQuoteProvider cannot
                 * classify the destination and rates the project's stored address instead, so a
                 * shopper switching between a home and an office sees the same residential pricing for
                 * each. Nothing here is persisted - saveShipping patches the address, and that is what
                 * sets the flag the order is actually rated on.
                 */
                address: address.address,
                address2: address.address2,
                city: address.city,
                totalOunces: NO_WEIGHT,
            }),
            projectService.getShipping(
                checkout.project.id,
                address.country ? address.country.id : null,
                address.state ? address.state.id : null,
                address.postalCode
            ),
        ]);

        Object.assign(checkout, {
            rates: buildRateList({
                quoteResponse,
                project: checkout.project,
                settings: checkout.settings,
                shipment: checkout.shipment,
            }),
            ...toGeography(shippingData),
        });

        // The setter validates against the new list, so a stale selection is refused rather than kept.
        const stillAvailable = previousSelectionKey
            ? checkout.rates.find(rate => rate.key === previousSelectionKey)
            : null;

        // Assigned through the checkout's own validating setter, which refuses a rate that is not in
        // the freshly fetched list.
        Object.assign(checkout, { shippingService: stillAvailable || findDefaultRate(checkout.rates, checkout.shipment) });

        return checkout.rates;
    }

    /**
     * List the shopper's saved payment methods - both vaulted cards and vaulted PayPal accounts.
     *
     * This needs no Braintree JavaScript. Pass a returned token to
     * {@link Collaterate.Checkout.setPayment setPayment} to pay with it.
     *
     * @param {number} userId - The signed-in user's id. Another user's id is rejected by the API.
     * @returns {Promise<SavedPaymentMethod[]>} Promise that resolves with the shopper's {@link SavedPaymentMethod SavedPaymentMethod}s.
     * @memberof Collaterate.Checkout
     * @example
     * const methods = await Collaterate.Checkout.getSavedPaymentMethods(user.id);
     *
     * methods.forEach(method => console.log(method.isPayPal
     *     ? `PayPal: ${method.name}`
     *     : `${method.cardType} ending ${method.last4}`));
     */

    static async getSavedPaymentMethods (userId) {
        typeCheck(userId, Number);

        const methods = await paymentMethodService.getAll(userId);

        // The API emits null for payment method types it does not map (Venmo, Apple Pay).
        return Object.freeze((methods || [])
            .filter(Boolean)
            .map(method => new SavedPaymentMethod(method)));
    }

    /**
     * Save a payment method to the shopper's Braintree vault, so they can reuse it on future orders.
     *
     * Vaulting happens server side with Collaterate's own Braintree credentials, so the same nonce you
     * would pay with is sufficient here - no client token is involved. Vault first, then pay with the
     * returned token via {@link Collaterate.Checkout.setPayment setPayment}.
     *
     * A newly vaulted card always becomes the shopper's default payment method. The API applies that
     * unconditionally and offers no way to opt out, so do not offer a "set as default" choice for a new
     * card - it can only ever be on. Changing the default afterwards is
     * {@link Collaterate.Checkout.updatePaymentMethod updatePaymentMethod}'s job.
     *
     * @param {number} userId - The signed-in user's id. Another user's id is rejected by the API.
     * @param {Collaterate.Checkout~savePaymentMethodOptions} paymentMethodOptions - The method to vault.
     * @returns {Promise<SavedPaymentMethod>} Promise that resolves with the vaulted {@link SavedPaymentMethod SavedPaymentMethod}.
     * @throws {CollaterateInputErrorList} If the card could not be verified.
     * @memberof Collaterate.Checkout
     * @example
     * const payload = await fields.tokenize();
     *
     * const saved = await Collaterate.Checkout.savePaymentMethod(user.id, {
     *     nonce: payload.nonce,
     *     cardholderName: 'Dan Piscitiello',
     * });
     *
     * Collaterate.Checkout.setPayment(checkout, {
     *     type: 'CREDIT_CARD',
     *     savedPaymentMethodToken: saved.token,
     * });
     */

    static async savePaymentMethod (userId, paymentMethodOptions) {
        typeCheck(userId, Number);
        typeCheck(paymentMethodOptions, Object);

        const { nonce, cardholderName = '' } = paymentMethodOptions;

        typeCheck(nonce, String);
        typeCheck(cardholderName, String);

        return new SavedPaymentMethod(await paymentMethodService.create(userId, { nonce, cardholderName }));
    }

    /**
     * Change a saved card in the shopper's Braintree vault, or make it their default payment method.
     *
     * Only the fields you pass are changed; anything omitted is left alone. The changeable fields are
     * `cardholderName`, `expirationDate`, `postalCode`, `deviceData` and `isDefault` - everything else
     * a {@link SavedPaymentMethod SavedPaymentMethod} exposes is read only, and passing one is
     * rejected rather than silently ignored.
     *
     * Two things to know before wiring this to a button:
     *
     * - **Cards only.** A vaulted PayPal account cannot be updated, and cannot be made the default.
     *   Because {@link Collaterate.Checkout.getSavedPaymentMethods getSavedPaymentMethods} returns
     *   cards and PayPal accounts in one list, a per-row control needs to skip the PayPal rows. This
     *   method reads the shopper's wallet first so a PayPal token fails with a clear error instead of
     *   a gateway one, which costs one extra request per call.
     * - **Every update re-verifies the card with Braintree.** Even a plain "make default" runs a live
     *   verification, so it can fail on a card that has since expired or that the issuer now declines.
     *   Handle the rejection; do not treat this as a local flag flip.
     *
     * @param {number} userId - The signed-in user's id. Another user's id is rejected by the API.
     * @param {string} token - The vault token of the card to change.
     * @param {Collaterate.Checkout~updatePaymentMethodOptions} changes - The fields to change.
     * @returns {Promise<SavedPaymentMethod>} Promise that resolves with the updated {@link SavedPaymentMethod SavedPaymentMethod}.
     * @throws {CollaterateInputErrorList} If no changes were given, a field is not changeable, the token is not the shopper's, it is a PayPal account, or the card could not be verified.
     * @memberof Collaterate.Checkout
     * @example
     * // "Make default" on a saved card
     * await Collaterate.Checkout.updatePaymentMethod(user.id, method.token, { isDefault: true });
     * @example
     * // Correcting the details on a card already in the vault
     * await Collaterate.Checkout.updatePaymentMethod(user.id, method.token, {
     *     cardholderName: 'Dan Piscitiello',
     *     expirationDate: '09/2030',
     *     postalCode: '55317',
     * });
     */

    static async updatePaymentMethod (userId, token, changes) {
        typeCheck(userId, Number);
        typeCheck(token, String);
        typeCheck(changes, Object);

        const requested = Object.keys(changes);
        const unchangeable = requested.filter(field => !UPDATABLE_FIELDS.includes(field));
        const errors = [];

        if (!requested.length) {
            errors.push({
                field: 'changes',
                message: `Nothing to change. Pass at least one of ${UPDATABLE_FIELDS.join(', ')}.`,
            });
        }

        unchangeable.forEach(field => errors.push({
            field,
            message: `"${field}" cannot be changed. Changeable fields are ${UPDATABLE_FIELDS.join(', ')}.`,
        }));

        if (errors.length) {
            throw clientInputError(errors);
        }

        /*
         * The update goes to Braintree's credit card API, so a PayPal token reaches it as a missing
         * card and comes back as a gateway error rather than something a consumer can act on. Looking
         * the token up first also catches a token that is not in this shopper's wallet at all.
         */
        const method = (await CheckoutAPI.getSavedPaymentMethods(userId))
            .find(savedMethod => savedMethod.token === token);

        if (!method) {
            throw clientInputError([{
                field: 'token',
                message: 'No saved payment method with that token. It may have been removed already.',
            }]);
        }

        if (method.isPayPal) {
            throw clientInputError([{
                field: 'token',
                message: 'A vaulted PayPal account cannot be updated or made the default. This works for saved cards only.',
            }]);
        }

        return new SavedPaymentMethod(await paymentMethodService.update(userId, token, changes));
    }

    /**
     * Remove a payment method from the shopper's Braintree vault.
     *
     * @param {number} userId - The signed-in user's id. Another user's id is rejected by the API.
     * @param {string} token - The vault token of the method to remove.
     * @returns {Promise} Promise that resolves on success.
     * @memberof Collaterate.Checkout
     * @example
     * await Collaterate.Checkout.deletePaymentMethod(user.id, method.token);
     */

    static async deletePaymentMethod (userId, token) {
        typeCheck(userId, Number);
        typeCheck(token, String);

        return paymentMethodService.remove(userId, token);
    }

    /**
     * Choose how this order will be paid for.
     *
     * Values are Braintree's own, not Collaterate's wire format - the SDK translates. Supply either a
     * `paymentMethodNonce` for a newly entered card or a `savedPaymentMethodToken` for a saved method,
     * never both. Terms needs neither.
     *
     * @param {Checkout} checkout - The checkout to set payment on. Updated in place.
     * @param {Collaterate.Checkout~paymentOptions} paymentOptions - The payment selection.
     * @returns {CheckoutPayment} The validated {@link CheckoutPayment CheckoutPayment} that was set.
     * @throws {CollaterateInputErrorList} If the selection is not valid, e.g. both or neither token, or an unsupported payment type.
     * @memberof Collaterate.Checkout
     * @example
     * // Terms
     * Collaterate.Checkout.setPayment(checkout, { type: 'TERMS', po: 'PO-4471' });
     *
     * @example
     * // A saved card or vaulted PayPal account - no Braintree JavaScript needed
     * const methods = await Collaterate.Checkout.getSavedPaymentMethods(user.id);
     * const method = methods[0];
     *
     * Collaterate.Checkout.setPayment(checkout, {
     *     type: method.isPayPal ? 'PAYPAL' : 'CREDIT_CARD',
     *     savedPaymentMethodToken: method.token,
     * });
     *
     * @example
     * // A newly entered card. You own the braintree-web lifecycle; the SDK only takes the result.
     * const client    = await braintree.client.create({ authorization: TOKENIZATION_KEY });
     * const fields    = await braintree.hostedFields.create({ client, fields: yourFieldSelectors });
     * const collector = await braintree.dataCollector.create({ client });
     * const payload   = await fields.tokenize();
     *
     * Collaterate.Checkout.setPayment(checkout, {
     *     type: 'CREDIT_CARD',
     *     cardHolder: 'Dan Piscitiello',
     *     paymentMethodNonce: payload.nonce,
     *     braintreeCardType: payload.details.cardType,
     *     lastTwo: payload.details.lastTwo,
     *     deviceData: collector.deviceData,
     * });
     */

    static setPayment (checkout, paymentOptions) {
        typeCheck(checkout, Checkout);
        typeCheck(paymentOptions, Object);

        const payment = new CheckoutPayment(paymentOptions);

        checkout.payment = payment;

        return payment;
    }

    /**
     * Save the contact details and order metadata for this checkout.
     *
     * @param {Checkout} checkout - The checkout to save.
     * @returns {Promise<Checkout>} Promise that resolves with the same {@link Checkout Checkout}.
     * @throws {CollaterateInputErrorList} If any field fails validation.
     * @memberof Collaterate.Checkout
     * @example
     * checkout.firstName = 'Dan';
     * checkout.lastName = 'Piscitiello';
     * checkout.email = 'dan@example.com';
     * checkout.phone = '555-0100';
     * checkout.orderMetadata['Order Type'] = 'Restock Request';
     *
     * await Collaterate.Checkout.saveContact(checkout);
     */

    static async saveContact (checkout) {
        typeCheck(checkout, Checkout);

        const { firstName: fname, lastName: lname, email, phone } = checkout;

        // Mirrors order1Controller_rest.js, which updates the user's own record as well as the project.
        await userService.updateContactInfo({ fname, lname, email, phone });

        await projectService.patchProject(checkout.project.id, buildContactPatch(checkout));

        return checkout;
    }

    /**
     * Save the shipping selections for this checkout.
     *
     * The save is deliberately two-phase. Carriers price signature on delivery per package, so the flag
     * has to reach the server, and the rates have to be re-quoted against it, before the chosen service
     * is persisted - otherwise the shipment keeps a null surcharge. The SDK sequences this for you, and
     * the project's PATCHes are serialized so the two saves cannot race each other.
     *
     * @param {Checkout} checkout - The checkout to save.
     * @returns {Promise<Checkout>} Promise that resolves with the same {@link Checkout Checkout}.
     * @throws {CollaterateInputErrorList} If an address or service fails validation.
     * @memberof Collaterate.Checkout
     * @example
     * checkout.shipToAddress = checkout.shippingAddresses[0];
     * await Collaterate.Checkout.refreshRates(checkout);
     * checkout.shippingService = checkout.rates[0];
     * checkout.isSignatureOnDelivery = true;
     *
     * await Collaterate.Checkout.saveShipping(checkout);
     */

    static async saveShipping (checkout) {
        typeCheck(checkout, Checkout);

        await projectService.patchProject(checkout.project.id, buildShippingFlagsPatch(checkout));

        // Re-quote so the signature surcharge is priced before the service is persisted.
        if (checkout.isSignatureOnDelivery && checkout.shipToAddress) {
            await CheckoutAPI.refreshRates(checkout);
        }

        const shippingServicePatch = buildShippingServicePatch(checkout);

        if (shippingServicePatch) {
            await projectService.patchProject(checkout.project.id, { shippingService: shippingServicePatch });
        }

        return CheckoutAPI.refresh(checkout);
    }

    /**
     * Save the payment selection for this checkout. Nothing is charged here - the card is authorized
     * when the order is submitted.
     *
     * @param {Checkout} checkout - The checkout to save. Its {@link Checkout#payment payment} must be set.
     * @returns {Promise<Checkout>} Promise that resolves with the same {@link Checkout Checkout}.
     * @throws {CollaterateInputErrorList} If no payment is set, or a required billing address is missing.
     * @memberof Collaterate.Checkout
     * @example
     * checkout.billToAddress = checkout.billingAddresses[0];
     * Collaterate.Checkout.setPayment(checkout, { type: 'TERMS', po: 'PO-4471' });
     *
     * await Collaterate.Checkout.savePayment(checkout);
     */

    static async savePayment (checkout) {
        typeCheck(checkout, Checkout);
        typeCheck(checkout.payment, CheckoutPayment);

        /*
         * Order matters and the API gives no hint of it. ProjectV2Provider#updatePaymentInfo reads the
         * payment type off the project before touching the card: with a non-card type it returns 200 and
         * silently drops the card, and with no type at all it fails with a 500. So the type is patched
         * first, always, and only then is the card stored.
         */
        await projectService.patchProject(checkout.project.id, buildPaymentPatch(checkout));

        const paymentInfo = buildPaymentInfo(checkout);

        if (paymentInfo) {
            await projectService.updateCreditCard(checkout.project.id, paymentInfo);
        }

        return checkout;
    }

    /**
     * Submit the order.
     *
     * For card and PayPal payments this authorizes the payment - it holds the funds, it does not
     * capture them. A returned {@link Order Order} means the order was placed and the payment
     * authorized, not that it has settled.
     *
     * @param {Checkout} checkout - The checkout to submit.
     * @returns {Promise<Order>} Promise that resolves with the created {@link Order Order}.
     * @throws {CheckoutError} If the order could not be placed. Read {@link CheckoutError#step step} to
     * decide where to send the shopper back to, and {@link CheckoutError#requiresNewPaymentMethod
     * requiresNewPaymentMethod} to know whether a fresh payment token is needed before retrying.
     * @memberof Collaterate.Checkout
     * @example
     * try {
     *     const order = await Collaterate.Checkout.submit(checkout);
     *     console.log(`Order ${order.number} placed for ${order.displayTotalPrice}.`);
     * } catch (error) {
     *     console.error(error.message);        // safe to show the shopper
     *     console.log(error.step);             // "PAYMENT", "SHIPPING", ...
     *
     *     if (error.requiresNewPaymentMethod) {
     *         // The nonce is spent. Collect a new one before trying again.
     *     }
     * }
     */

    static async submit (checkout) {
        typeCheck(checkout, Checkout);

        const orderData = await projectService.order(checkout.project.id).catch(toCheckoutError);

        return new Order(orderData);
    }
}

/**
 * The checkout steps a {@link CheckoutError CheckoutError} can point back to: `CONTACT`, `SHIPPING`,
 * `PAYMENT` and `REVIEW`.
 *
 * @name Collaterate.Checkout.checkoutSteps
 * @type {Object}
 * @memberof Collaterate.Checkout
 * @example
 * try {
 *     await Collaterate.Checkout.submit(checkout);
 * } catch (error) {
 *     if (error.step === Collaterate.Checkout.checkoutSteps.SHIPPING) {
 *         showShippingStep();
 *     }
 * }
 */

CheckoutAPI.checkoutSteps = checkoutSteps;

/**
 * The payment types accepted by {@link Collaterate.Checkout.setPayment setPayment}: `CREDIT_CARD`,
 * `PAYPAL` and `TERMS`.
 *
 * @name Collaterate.Checkout.paymentTypes
 * @type {Object}
 * @memberof Collaterate.Checkout
 * @example
 * Collaterate.Checkout.setPayment(checkout, {
 *     type: Collaterate.Checkout.paymentTypes.TERMS,
 *     po: 'PO-4471',
 * });
 */

CheckoutAPI.paymentTypes = paymentTypes;

/**
 * A payment method to add to the shopper's vault.
 *
 * @typedef {Object} Collaterate.Checkout~savePaymentMethodOptions
 * @property {string} nonce - A single-use Braintree nonce for the card to vault.
 * @property {string} [cardholderName] - The name on the card.
 */

/**
 * Changes to make to a saved card. Every field is optional, but at least one is required; the fields
 * you leave out keep the values they have. Nothing else a {@link SavedPaymentMethod SavedPaymentMethod}
 * exposes can be changed.
 *
 * @typedef {Object} Collaterate.Checkout~updatePaymentMethodOptions
 * @property {boolean} [isDefault] - Pass true to make this the shopper's default payment method. Prefer promoting a different card over trying to clear a default, which has no meaning on its own.
 * @property {string} [cardholderName] - The name on the card.
 * @property {string} [expirationDate] - The card expiration, as "MM/YYYY".
 * @property {string} [postalCode] - The billing postal code to keep on file with the card.
 * @property {string} [deviceData] - A fresh payload from `braintree.dataCollector`, for fraud scoring on the verification this update performs.
 */

/**
 * How an order will be paid for. Card values come straight from Braintree.
 *
 * @typedef {Object} Collaterate.Checkout~paymentOptions
 * @property {string} type - "CREDIT_CARD", "PAYPAL" or "TERMS". Saved cards use "CREDIT_CARD"; vaulted PayPal accounts use "PAYPAL".
 * @property {string} [po] - The purchase order number, for terms payments.
 * @property {string} [paymentMethodNonce] - A single-use Braintree nonce for a newly entered card. Never a card number.
 * @property {string} [savedPaymentMethodToken] - A vault token from {@link Collaterate.Checkout.getSavedPaymentMethods getSavedPaymentMethods}.
 * @property {string} [cardHolder] - The name on the card. Ignored for saved methods, which the server resolves itself.
 * @property {string} [braintreeCardType] - Braintree's own card type string, e.g. `payload.details.cardType`.
 * @property {string} [lastTwo] - The last two digits of the card, e.g. `payload.details.lastTwo`.
 * @property {string} [deviceData] - The payload from `braintree.dataCollector`. Strongly recommended for new cards; Braintree's fraud tooling depends on it.
 */
